/*
 * Lets users optionally give a Box folder a default locale, which Bender assigns to assets whose file
 * name and folders name none. Shared by the add and edit modals so both offer exactly Bender's locale
 * vocabulary and map the "None" choice to `null` the same way.
 */
import React from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useTranslation } from 'src/arb-wrapper';
import { SYNC_TARGET_LOCALES, type SyncTargetLocale } from 'src/constants';

/** Select option values are strings, so an empty one stands in for "no default locale". */
const NO_DEFAULT_LOCALE = '';

const toDefaultLocale = (value: string): SyncTargetLocale | null =>
  SYNC_TARGET_LOCALES.find((locale) => locale === value) ?? null;

interface BoxFolderDefaultLocaleSelectProps {
  isDisabled: boolean;
  /** The test id is `${testIdPrefix}-default-locale-select`. */
  testIdPrefix: string;
  value: SyncTargetLocale | null;
  onChange: (locale: SyncTargetLocale | null) => void;
}

export const BoxFolderDefaultLocaleSelect: React.FC<BoxFolderDefaultLocaleSelectProps> = ({
  isDisabled,
  testIdPrefix,
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Select
      data-testid={`${testIdPrefix}-default-locale-select`}
      disabled={isDisabled}
      helperText={t('box_folder_default_locale_help')}
      label={t('box_folder_default_locale_label')}
      value={value ?? NO_DEFAULT_LOCALE}
      onChange={(next: string) => { onChange(toDefaultLocale(next)); }}
    >
      <SelectOption label={t('box_folder_default_locale_none')} value={NO_DEFAULT_LOCALE} />
      {SYNC_TARGET_LOCALES.map((locale) => (
        <SelectOption key={locale} label={locale} value={locale} />
      ))}
    </Select>
  );
};
